import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { IoIosSend } from "react-icons/io";
import { FaMicrophone } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { MdMic, MdSearch } from "react-icons/md";
import LoadingDiv from "./LoadingDiv";
import ChatMessage from "./ChatMessage";
import Tooltip from "./Tooltip";
import { formatDuration } from "../utils";
import { formatTimestamp } from "../utils/chatUtils";
import "./Chat.css";

const Chat = ({
  chat,
  onSendMessage,
  isLoading,
  token,
  api,
  onDeleteMessage,
  onPlayEmote,
  isMobile,
  assistantTalking,
  selectedModel,
}) => {
  // Display label for the typing indicator: internal model name stays
  // Echo/Synth but the visible label is Legacy/Story per the rebrand.
  const personaLabel = selectedModel === 'Synth' ? 'Story' : 'Legacy';
  // Input and UI state
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showSendTooltip, setShowSendTooltip] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  // Recording / speech recognition state
  const [isRecording, setIsRecording] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isListening, setIsListening] = useState(false);
  
  // Message reactions state
  const [messageReactions, setMessageReactions] = useState({});
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const recognitionRef = useRef(null);
  const pendingTranscriptRef = useRef('');

  // Auto-mute when assistant starts talking
  useEffect(() => {
    if (assistantTalking) {
      if (isListening && recognitionRef.current) {
        recognitionRef.current.abort();
        setIsListening(false);
        setNewMessage('');
        pendingTranscriptRef.current = '';
      }
      if (isCountingDown && countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        setIsCountingDown(false);
        setCountdownValue(3);
      }
      if (isRecording) {
        cancelRecording();
      }
    }
  }, [assistantTalking, isRecording, isCountingDown, isListening]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      stopRecording();
    };
  }, []);

  // Show the "typing" indicator for the WHOLE time a reply is being generated — not a fixed
  // 3s. The old timeout flipped the typing dots off after 3s and a different spinner
  // (LoadingDiv) took over mid-wait — a jarring two-loader swap, made obvious now that replies
  // take >3s (Soul LM + grounded rewrite). One indicator, tied to the real load = smooth.
  useEffect(() => {
    setIsTyping(isLoading);
  }, [isLoading]);

  // Handle message reactions
  const handleReact = useCallback((messageId, reactionType) => {
    setMessageReactions(prev => {
      const messageReactions = prev[messageId] || {};
      const currentCount = messageReactions[reactionType] || 0;
      
      return {
        ...prev,
        [messageId]: {
          ...messageReactions,
          [reactionType]: currentCount + 1
        }
      };
    });
  }, []);

  // Filter messages based on search query
  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return chat;
    return chat.filter(message => 
      message.content && 
      message.content.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [chat, searchQuery]);

  // Process messages to optimize performance
  const processedMessages = useMemo(() => {
    // Limit total number of messages to prevent performance issues
    const maxDisplayMessages = 100;
    const limitedMessages = filteredMessages.slice(-maxDisplayMessages);
    
    return limitedMessages;
  }, [filteredMessages]);

  // Group consecutive messages from the same sender
  const groupedMessages = useMemo(() => {
    return processedMessages.reduce((groups, message, index) => {
      const prevMessage = processedMessages[index - 1];
      const shouldGroup = prevMessage && 
                         prevMessage.role === message.role && 
                         message.timestamp && prevMessage.timestamp &&
                         (message.timestamp - prevMessage.timestamp) < 300;
      
      groups.push({
        ...message,
        isGrouped: shouldGroup,
        showTimestamp: !shouldGroup || index === processedMessages.length - 1
      });
      
      return groups;
    }, []);
  }, [processedMessages]);

  // Message sending with size limit
  const handleSendMessage = useCallback(() => {
    if (newMessage.trim()) {
      // Limit message size to prevent issues
      const maxMessageSize = 10000;
      let messageToSend = newMessage;
      
      if (messageToSend.length > maxMessageSize) {
        messageToSend = messageToSend.substring(0, maxMessageSize) + 
          "\n\n[Message truncated due to size limits]";
        console.warn("Message truncated due to size limits");
      }
      
      onSendMessage(messageToSend);
      setNewMessage("");
    }
  }, [newMessage, onSendMessage]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Speech-to-text using Web Speech API
  const handleMicClick = useCallback(() => {
    if (assistantTalking) {
      console.log("🔇 Cannot start listening: Assistant is speaking");
      return;
    }

    // If already listening, stop
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error('SpeechRecognition not supported in this browser');
      // Fallback: use old recording flow
      handleRecordClickFallback();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;
    pendingTranscriptRef.current = '';

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      if (finalTranscript) {
        pendingTranscriptRef.current += finalTranscript;
      }
      setNewMessage(pendingTranscriptRef.current + interimTranscript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      // Auto-submit if we have text
      const text = pendingTranscriptRef.current.trim();
      if (text && typeof onSendMessage === 'function') {
        onSendMessage(text);
        setNewMessage('');
        pendingTranscriptRef.current = '';
      }
    };

    recognition.start();
  }, [assistantTalking, isListening, onSendMessage]);

  // Fallback recording for browsers without SpeechRecognition
  const handleRecordClickFallback = useCallback(() => {
    setIsCountingDown(true);
    setCountdownValue(3);
    countdownTimerRef.current = setInterval(() => {
      setCountdownValue((prev) => {
        if (prev === 1) {
          clearInterval(countdownTimerRef.current);
          setIsCountingDown(false);
          startRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      setRecordingTime(0);
      recordingStartTimeRef.current = Date.now();
      recordingTimerRef.current = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
        setRecordingTime(elapsedSeconds);
      }, 200);
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      clearInterval(recordingTimerRef.current);
      
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setIsRecording(false);
    audioChunksRef.current = [];
  };

  const sendRecording = () => {
    mediaRecorderRef.current.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = function() {
        const base64Audio = reader.result.split(',')[1];
        const audioMessage = {
          role: "user",
          type: "AUDIO_MESSAGE",
          audioUrl: audioUrl,
          audioData: base64Audio,
          timestamp: Math.floor(Date.now() / 1000)
        };
        setIsRecording(false);
        audioChunksRef.current = [];
        if (typeof onSendMessage === 'function') {
          onSendMessage(audioMessage);
        }
      };
    };
    stopRecording();
  };

  // Render different message types
  const renderMessage = (message, index) => {
    if (message.type === "CONVERSATION_EVENT") {
      return (
        <div key={index} className="message-system-event">
          <span className="message-system-event-icon">📞</span>
          <span>{message.content || 'Phone call'}</span>
          {message.duration && <span className="message-system-event-duration">{message.duration}</span>}
        </div>
      );
    }
    
    if (message.type === "DATE_SEPARATOR") {
      return (
        <div key={index} className="message-date-separator">
          <div className="date-separator-text">
            {message.content}
          </div>
          <div className="date-separator-line" />
        </div>
      );
    }
    
    if (message.type === "memory_confirmation") {
      return (
        <div key={index} className="message-memory-confirmation">
          🧠 {message.content}
        </div>
      );
    }

    // Proactive (AI-initiated) messages get a distinct style
    if (message.isProactive && message.role === 'assistant') {
      return (
        <div key={index} className="message-proactive-wrapper">
          <div className="message-proactive-label">AI Check-in</div>
          <div className="message-proactive-bubble">{message.content}</div>
        </div>
      );
    }

    const messageWithReactions = {
      ...message,
      reactions: messageReactions[message.id || `${index}-${message.timestamp}`] || {}
    };

    return (
      <ChatMessage 
        key={message.id || `${index}-${message.timestamp}`}
        onDeleteMessage={onDeleteMessage} 
        api={api} 
        token={token} 
        message={messageWithReactions} 
        index={index} 
        onPlayEmote={onPlayEmote}
        isMobile={isMobile}
        onReact={handleReact}
        isGrouped={message.isGrouped}
        showTimestamp={message.showTimestamp}
      />
    );
  };

  return (
    <div className="chat-container">
      {/* Search Header */}
      <div className="search-header">
        <button
          onClick={() => setShowSearch(!showSearch)}
          className={`search-button ${showSearch ? 'active' : ''}`}
          title="Search messages"
        >
          <MdSearch size={16} />
        </button>
        
        {showSearch && (
          <input
            type="text"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            autoFocus
          />
        )}
        
        {searchQuery && (
          <span className="search-results">
            {filteredMessages.length} result{filteredMessages.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Messages Container */}
      <div className="messages-container">
        {groupedMessages.length === 0 && (
          <div className="no-messages">
            {searchQuery ? (
              <>
                <p className="no-messages-title">Nothing matched "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="clear-search"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <p className="no-messages-title">Say hello</p>
                <p className="no-messages-sub">
                  Ask {personaLabel} anything — about a memory, a feeling,
                  or just how the day's going.
                </p>
              </>
            )}
          </div>
        )}
        
        {groupedMessages.map((message, index) => renderMessage(message, index))}
        
        {/* Typing Indicator */}
        {isTyping && (
          <div className="typing-indicator">
            <div className="typing-indicator-content">
              <span className="typing-indicator-text">{personaLabel} is thinking</span>
              <div className="typing-dots">
                {[0, 1, 2].map(i => (
                  <div key={i} className="typing-dot" />
                ))}
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Loading indicator */}
      {isLoading && !isTyping && (
        <div className="loading-container">
          <LoadingDiv
            isLoading 
            duration={0.75} 
            width="25px"
            height="25px"
            borderWidth={1}
            loadingColor="#FFFFFF"
            borderColor="rgba(255, 255, 255, 0.5)"
            borderRadius="10px"
            backgroundColor="transparent"
            loadingSegmentPercentage={25}
          />
        </div>
      )}
      
      {/* Input Container */}
      <div className="input-container">
        {/* Text Input Area */}
        {isRecording ? (
          <div className="recording-indicator">
            <div className="recording-left">
              <MdMic size={20} color="#dc2626" className="recording-icon" />
              <span className="recording-text">Recording...</span>
            </div>
            <div className="recording-right">
              <div className="recording-dot" />
              {formatDuration(recordingTime)}
            </div>
          </div>
        ) : (
          <div className="input-wrapper">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your message..."
              className={`text-input ${isMobile ? 'mobile' : ''}`}
            />
            {newMessage.length > 100 && (
              <div className="character-count">
                {newMessage.length}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons Container */}
        <div className="action-buttons-container">
          {/* Microphone Button */}
          {!isRecording && (
            <div className="mic-button-container">
              <button
                onClick={handleMicClick}
                disabled={assistantTalking}
                className={`action-button record ${isListening ? 'listening' : ''} ${isCountingDown ? 'countdown' : ''} ${assistantTalking ? 'disabled' : ''}`}
                title={isListening ? 'Click to stop and send' : assistantTalking ? 'Cannot record while AI is speaking' : 'Speak to type'}
              >
                {isCountingDown ? (
                  countdownValue
                ) : (
                  <FaMicrophone size={18} />
                )}
              </button>
              
              {assistantTalking && (
                <div className="assistant-talking-indicator">
                  ✕
                </div>
              )}
            </div>
          )}

          {/* Send Button */}
          <Tooltip 
            text={isRecording ? "Send Voice Message" : "Send Message"} 
            show={showSendTooltip || (isRecording && !isMobile)}
          >
            <button
              onClick={isRecording ? sendRecording : handleSendMessage}
              onMouseEnter={() => setShowSendTooltip(true)}
              onMouseLeave={() => setShowSendTooltip(false)}
              disabled={!isRecording && !newMessage.trim()}
              className={`action-button send ${(!isRecording && !newMessage.trim()) ? 'disabled' : ''}`}
            >
              <IoIosSend size={20} />
            </button>
          </Tooltip>
          
          {/* Cancel button when recording */}
          {isRecording && (
            <button
              onClick={cancelRecording}
              className="action-button cancel"
              title="Cancel Recording"
            >
              <ImCross size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;
